import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Card,
  Header,
  Eyebrow,
  Title,
  Subtitle,
  SectionLabel,
  GradeGrid,
  GradeField,
  GradeLabel,
  Input,
  CalendarWrap,
  CalendarToolbar,
  ToolbarButton,
  MonthTitle,
  CalendarGrid,
  WeekdayCell,
  DayCell,
  DayNumber,
  EmptyCell,
  Hint,
  LegendRow,
  LegendItem,
  LegendSwatch,
  StatsBar,
  StatBlock,
  StatValue,
  StatLabel,
  Btt02,
  ErrorMessage
} from './style';
import { RegisterIVthQuarter, RegisterIVthQuarter$grade, getSchoolYear } from '../../Api';
import { useNavigate } from 'react-router-dom';
import ResponsivePickers from '../../components/Datebimestre';
import { SeletorMarcacao, LegendaNaoLetivos, mapaNaoLetivos, contarNaoLetivos, estiloMarcacao, tituloDoDia, TIPOS_DIA } from '../../components/NonSchoolDays';
import LoadingSpinner from '../../components/Loading';

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const keyFromParts = (y, m0, d) =>
  `${y}-${String(m0 + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

// Lê a chave de uma data (Date ou ISO) em UTC, evitando deslocar o dia em fuso negativo
const toKey = (value) => {
  const dt = new Date(value);
  if (isNaN(dt.getTime())) return null;
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
};

const HomeSchool = () => {
  const navigate = useNavigate();
  const [year, setyear] = useState('');
  const [id_school, setIdSchool] = useState('');
  const [assessmentFormat, setassessmentFormat] = useState('');
  const [assessmentRegime, setAssessmentRegime] = useState('');
  const [loading, setLoading] = useState(true);
  const [startSelectedDate, setStartSelectedDate] = useState('');
  const [startday, setStartDay] = useState('');
  const [startmonth, setStartMonth] = useState('');
  const [startyear, setStartYear] = useState('');
  const [endSelectedDate, setEndSelectedDate] = useState('');
  const [endday, setEndDay] = useState('');
  const [endmonth, setEndMonth] = useState('');
  const [endyear, setEndYear] = useState('');
  const [totalGrade, setTotalGrade] = useState('');
  const [averageGrade, setAverageGrade] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  // schoolDays: array de { date, isSchoolDay } — mesma estrutura do original
  const [schoolDays, setSchoolDays] = useState([]);
  const [viewDate, setViewDate] = useState(new Date());
  // feriados, recessos e outros dias sem aula: [{ date, type, description }]
  const [nonSchoolDays, setNonSchoolDays] = useState([]);
  const [markMode, setMarkMode] = useState('letivo');
  const [markDesc, setMarkDesc] = useState('');
  const nonMap = useMemo(() => mapaNaoLetivos(nonSchoolDays), [nonSchoolDays]);
  const contagem = useMemo(() => contarNaoLetivos(nonSchoolDays), [nonSchoolDays]);

  useEffect(() => {
    (async () => {
      const idSchool = sessionStorage.getItem('id-school');
      const schoolYear = await getSchoolYear(JSON.parse(idSchool));
      setyear(schoolYear.data.data);
      const $assessmentFormat = sessionStorage.getItem('assessmentFormat');
      setassessmentFormat($assessmentFormat);
      setAssessmentRegime(sessionStorage.getItem('assessmentRegime'));
      setIdSchool(JSON.parse(idSchool));
      setLoading(false);
    })();
  }, []);

  // Limites do período como chaves (ou null)
  const startKey = useMemo(() => {
    if (!startday || !startmonth || !startyear) return null;
    return keyFromParts(Number(startyear), Number(startmonth) - 1, Number(startday));
  }, [startday, startmonth, startyear]);

  const endKey = useMemo(() => {
    if (!endday || !endmonth || !endyear) return null;
    return keyFromParts(Number(endyear), Number(endmonth) - 1, Number(endday));
  }, [endday, endmonth, endyear]);

  const hasPeriod = startKey && endKey && endKey >= startKey;

  // Gera os dias do intervalo quando o período muda, preservando marcações já feitas
  useEffect(() => {
    if (!hasPeriod) {
      setSchoolDays([]);
      return;
    }
    const start = new Date(Number(startyear), Number(startmonth) - 1, Number(startday));
    const end = new Date(Number(endyear), Number(endmonth) - 1, Number(endday));

    setSchoolDays(prev => {
      // Preserva quais dias já estavam marcados
      const marked = new Set(prev.filter(d => d.isSchoolDay).map(d => toKey(d.date)));
      const days = [];
      const current = new Date(start);
      while (current <= end) {
        const y = current.getFullYear();
        const m0 = current.getMonth();
        const d = current.getDate();
        const iso = new Date(Date.UTC(y, m0, d)).toISOString();
        const key = keyFromParts(y, m0, d);
        days.push({ date: iso, isSchoolDay: marked.has(key), description: '' });
        current.setDate(current.getDate() + 1);
      }
      return days;
    });

    setViewDate(new Date(Number(startyear), Number(startmonth) - 1, 1));
  }, [hasPeriod, startday, startmonth, startyear, endday, endmonth, endyear]);

  // Mapa: chave do dia -> isSchoolDay
  const dayMap = useMemo(() => {
    const map = new Map();
    schoolDays.forEach(d => {
      const k = toKey(d.date);
      if (k) map.set(k, d.isSchoolDay);
    });
    return map;
  }, [schoolDays]);

  const toggleDay = (year_, month0, day) => {
    const targetKey = keyFromParts(year_, month0, day);
    setSchoolDays(prev =>
      prev.map(d =>
        toKey(d.date) === targetKey ? { ...d, isSchoolDay: !d.isSchoolDay } : d
      )
    );
  };

  // Clique no dia conforme o modo escolhido (letivo / feriado / recesso / outro)
  const handleDayClick = (y_, m0_, d_) => {
    const key = keyFromParts(y_, m0_, d_);
    if (markMode === 'letivo') {
      setNonSchoolDays(prev => prev.filter(n => toKey(n.date) !== key));
      toggleDay(y_, m0_, d_);
      return;
    }
    const atual = nonMap.get(key);
    setNonSchoolDays(prev => {
      const sem = prev.filter(n => toKey(n.date) !== key);
      if (atual && atual.type === markMode) return sem; // mesmo tipo: desmarca
      return [...sem, { date: new Date(Date.UTC(y_, m0_, d_)).toISOString(), type: markMode, description: markDesc.trim() }];
    });
    // dia marcado como feriado/recesso/outro deixa de ser letivo
    setSchoolDays(prev => prev.map(d => (toKey(d.date) === key ? { ...d, isSchoolDay: false } : d)));
  };

  // Grade do mês em exibição
  const calendarCells = useMemo(() => {
    const y = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(y, month, 1).getDay();
    const daysInMonth = new Date(y, month + 1, 0).getDate();

    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const key = keyFromParts(y, month, d);
      const weekday = new Date(y, month, d).getDay();
      const weekend = weekday === 0 || weekday === 6;
      const inRange = hasPeriod && key >= startKey && key <= endKey;
      const isLetivo = dayMap.get(key) === true;
      cells.push({ day: d, year: y, month0: month, key, isLetivo, weekend, inRange });
    }
    return cells;
  }, [viewDate, dayMap, hasPeriod, startKey, endKey]);

  const goPrevMonth = () =>
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  const goNextMonth = () =>
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));

  const totalLetivos = useMemo(
    () => schoolDays.filter(d => d.isSchoolDay).length,
    [schoolDays]
  );

  const onlySchoolDays = schoolDays.filter(day => day.isSchoolDay);
  // só os feriados/recessos/outros dentro do período
  const nonSchoolDaysPayload = nonSchoolDays
    .filter(n => { const k = toKey(n.date); return !hasPeriod || (k >= startKey && k <= endKey); })
    .map(n => ({ date: n.date, type: n.type, description: n.description || '' }));

  const setBimester = async () => {
    setErrorMessage('');

    if (
      !year || !startday || !startmonth || !startyear ||
      !endday || !endmonth || !endyear || !id_school
    ) {
      setErrorMessage('Preencha todos os campos obrigatórios do período.');
      return;
    }

    if (onlySchoolDays.length === 0) {
      setErrorMessage('Selecione ao menos um dia letivo no calendário.');
      return;
    }

    if (assessmentFormat === 'grade') {
      if (!totalGrade || !averageGrade) {
        setErrorMessage('Informe a nota total e a média para o período.');
        return;
      }
    }

    setLoading(true);

    if (assessmentFormat === 'grade') {
      const res = await RegisterIVthQuarter$grade(
        year,
        startday,
        startmonth,
        startyear,
        endday,
        endmonth,
        endyear,
        totalGrade,
        averageGrade,
        id_school,
        assessmentRegime,
        onlySchoolDays,
        nonSchoolDaysPayload
      );

      if (res) {
        alert('Período definido com sucesso');
        navigate(-1);
      } else {
        setErrorMessage('Erro ao cadastrar. Verifique os dados e tente novamente.');
      }
    } else {
      const res = await RegisterIVthQuarter(
        year,
        startday,
        startmonth,
        startyear,
        endday,
        endmonth,
        endyear,
        id_school,
        assessmentRegime,
        onlySchoolDays,
        nonSchoolDaysPayload
      );

      if (res) {
        alert('Período definido com sucesso');
        navigate(-1);
      } else {
        setErrorMessage('Erro ao cadastrar. Verifique os dados e tente novamente.');
      }
    }
    setLoading(false);
  };

  const periodoLabel = assessmentRegime === 'TRIMESTRAL' ? 'Trimestre' : 'Bimestre';

  return (
    <Container>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <Card>
          <Header>
            <Eyebrow>Definição de período</Eyebrow>
            <Title>4º {periodoLabel}</Title>
            <Subtitle>
              Defina o período e toque nos dias letivos no calendário.
            </Subtitle>
          </Header>

          <SectionLabel>Período do {periodoLabel.toLowerCase()}</SectionLabel>
          <ResponsivePickers
            schoolYear={year}
            selectedDateStart={startSelectedDate}
            selectedDateEnd={endSelectedDate}
            setSelectedDateStart={setStartSelectedDate}
            setDayStart={setStartDay}
            setMonthStart={setStartMonth}
            setYearStart={setStartYear}
            setSelectedDateEnd={setEndSelectedDate}
            setDayEnd={setEndDay}
            setMonthEnd={setEndMonth}
            setYearEnd={setEndYear}
          />

          {assessmentFormat === 'grade' && (
            <>
              <SectionLabel>Notas</SectionLabel>
              <GradeGrid>
                <GradeField>
                  <GradeLabel>Nota Total</GradeLabel>
                  <Input
                    placeholder="Ex.: 10,0"
                    value={totalGrade}
                    onChange={(e) => {
                      let value = e.target.value.replace('.', ',');
                      value = value.replace(/[^0-9,]/g, '');
                      if ((value.match(/,/g) || []).length <= 1) {
                        const numericValue = parseFloat(value.replace(',', '.'));
                        if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 100) {
                          setTotalGrade(value);
                        } else if (value === '') {
                          setTotalGrade('');
                        }
                      }
                    }}
                  />
                </GradeField>
                <GradeField>
                  <GradeLabel>Nota Média</GradeLabel>
                  <Input
                    placeholder="Ex.: 6,0"
                    value={averageGrade}
                    onChange={(e) => {
                      let value = e.target.value.replace('.', ',');
                      value = value.replace(/[^0-9,]/g, '');
                      if ((value.match(/,/g) || []).length <= 1) {
                        const numericValue = parseFloat(value.replace(',', '.'));
                        if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 100) {
                          setAverageGrade(value);
                        } else if (value === '') {
                          setAverageGrade('');
                        }
                      }
                    }}
                  />
                </GradeField>
              </GradeGrid>
            </>
          )}

          <SectionLabel>Dias letivos</SectionLabel>

          {!hasPeriod ? (
            <Hint>
              Defina a data de início e término acima para liberar a seleção
              dos dias letivos.
            </Hint>
          ) : (
            <CalendarWrap>
              <SeletorMarcacao modo={markMode} setModo={setMarkMode} descricao={markDesc} setDescricao={setMarkDesc} />
              <CalendarToolbar>
                <ToolbarButton type="button" onClick={goPrevMonth} aria-label="Mês anterior">
                  ‹
                </ToolbarButton>
                <MonthTitle>
                  {MONTH_NAMES[viewDate.getMonth()]} {viewDate.getFullYear()}
                </MonthTitle>
                <ToolbarButton type="button" onClick={goNextMonth} aria-label="Próximo mês">
                  ›
                </ToolbarButton>
              </CalendarToolbar>

              <CalendarGrid>
                {WEEKDAYS.map(w => (
                  <WeekdayCell key={w}>{w}</WeekdayCell>
                ))}
                {calendarCells.map((cell, idx) => {
                  if (!cell) return <EmptyCell key={`e-${idx}`} />;
                  const { day, year: cy, month0, key, isLetivo, weekend, inRange } = cell;
                  const mark = nonMap.get(key);
                  const interactive = inRange || isLetivo || !!mark;
                  return (
                    <DayCell
                      key={key}
                      type="button"
                      $active={isLetivo}
                      $weekend={weekend}
                      $disabled={!interactive}
                      disabled={!interactive}
                      onClick={() => handleDayClick(cy, month0, day)}
                      aria-pressed={isLetivo}
                      title={tituloDoDia(key, mark)}
                      style={mark ? estiloMarcacao(mark.type) : undefined}
                    >
                      <DayNumber>{day}</DayNumber>
                    </DayCell>
                  );
                })}
              </CalendarGrid>

              <LegendRow>
                <LegendItem>
                  <LegendSwatch $variant="active" /> Dia letivo
                </LegendItem>
                <LegendItem>
                  <LegendSwatch $variant="inactive" /> Disponível
                </LegendItem>
                <LegendaNaoLetivos Item={LegendItem} Swatch={LegendSwatch} />
                <LegendItem>
                  <LegendSwatch $variant="disabled" /> Fora do período
                </LegendItem>
              </LegendRow>
            </CalendarWrap>
          )}

          {hasPeriod && (
            <StatsBar>
              <StatBlock>
                <StatValue>{totalLetivos}</StatValue>
                <StatLabel>Dias letivos selecionados</StatLabel>
              </StatBlock>
              <StatBlock>
                  <StatValue style={{ color: TIPOS_DIA.feriado.cor }}>{contagem.feriado}</StatValue>
                  <StatLabel>Feriados</StatLabel>
              </StatBlock>
              <StatBlock>
                  <StatValue style={{ color: TIPOS_DIA.recesso.cor }}>{contagem.recesso}</StatValue>
                  <StatLabel>Recessos</StatLabel>
              </StatBlock>
              <StatBlock>
                  <StatValue style={{ color: TIPOS_DIA.outro.cor }}>{contagem.outro}</StatValue>
                  <StatLabel>Outros</StatLabel>
              </StatBlock>
            </StatsBar>
          )}

          {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

          <Btt02 onClick={setBimester}>Definir</Btt02>
        </Card>
      )}
    </Container>
  );
};

export default HomeSchool;